import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { MessageCircle, X, Minus, Send } from 'lucide-react';
import { sendChatMessage } from '../lib/api';
import { ChatMessageList } from './chat/ChatMessageList';

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && !minimized && inputRef.current) inputRef.current.focus();
  }, [open, minimized]);

  const send = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await sendChatMessage(userMsg, sessionId);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Désolé, une erreur est survenue." }]);
    }
    setLoading(false);
  }, [input, loading, sessionId]);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-blue-600 flex items-center justify-center transition-all hover:scale-110" data-testid="chat-toggle-btn">
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 w-80 sm:w-96 bg-card border border-border rounded-2xl shadow-2xl flex flex-col transition-all ${minimized ? 'h-12 overflow-hidden' : 'h-[500px]'}`} data-testid="chat-widget">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border cursor-pointer shrink-0" onClick={() => setMinimized(!minimized)}>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-primary" />
          <span className="font-bold text-sm">Assistant QCM</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }} className="text-muted-foreground hover:text-foreground p-1" data-testid="chat-minimize-btn">
            <Minus className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="text-muted-foreground hover:text-foreground p-1" data-testid="chat-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            <ChatMessageList messages={messages} loading={loading} />
          </div>
          <div className="border-t border-border p-3 shrink-0">
            <div className="flex gap-2">
              <input ref={inputRef} type="text" className="flex-1 bg-secondary rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50 placeholder-muted-foreground"
                placeholder="Posez une question..." value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={loading} data-testid="chat-input" />
              <Button onClick={send} disabled={!input.trim() || loading} size="sm" className="bg-primary text-primary-foreground" data-testid="chat-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
